// src/context/AppContext.jsx

import { useContext } from "react";
import { useState } from "react";
import { createContext } from "react";
import { ApiContext } from "../App";
import { toast } from "react-toastify";
import { useEffect } from "react";

export const AppContent = createContext()

export const AppContextProvider = (props) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL
    const [isLoggedin, setIsLoggedin] = useState(false)
    const [userData, setUserData] = useState(false)
    const API = useContext(ApiContext)
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);

    // Ajout pour la gestion des utilisateurs
    const [usersMap, setUsersMap] = useState({});
    const [usersLoading, setUsersLoading] = useState(true);
    const [usersError, setUsersError] = useState(null);

    // Fonction pour récupérer les notifications
    const fetchNotifications = async () => {
        try {
            const { data } = await API.get('/api/notifications');
            if (data.success) {
                setNotifications(data.notifications);
                const unread = data.notifications.filter(n => !n.isRead).length;
                setUnreadCount(unread);
            }
        } catch (error) {
            console.error('Erreur lors de la récupération des notifications:', error);
        }
    };

    // Marquer une notification comme lue
    const markAsRead = async (notificationId) => {
        try {
            const { data } = await API.put(`/api/notifications/${notificationId}/read`);
            if (data.success) {
                fetchNotifications();
            }
        } catch (error) {
            console.error('Erreur lors du marquage de la notification:', error);
        }
    };

    // Marquer une notification comme non lue
    const markAsUnread = async (notificationId) => {
        try {
            const { data } = await API.put(`/api/notifications/${notificationId}/unread`);
            if (data.success) {
                fetchNotifications();
            }
        } catch (error) {
            console.error('Erreur lors du marquage de la notification:', error);
        }
    };

    // Supprimer une notification
    const deleteNotification = async (notificationId) => {
        try {
            const { data } = await API.delete(`/api/notifications/${notificationId}`);
            if (data.success) {
                fetchNotifications();
                toast.success('Notification supprimée');
            }
        } catch (error) {
            console.error('Erreur lors de la suppression:', error);
            toast.error('Erreur lors de la suppression');
        }
    };

    // Marquer toutes comme lues
    const markAllAsRead = async () => {
        try {
            const { data } = await API.put('/api/notifications/read-all');
            if (data.success) {
                fetchNotifications();
            }
        } catch (error) {
            console.error('Erreur:', error);
        }
    };

    // Effet pour charger les notifications
    useEffect(() => {
        if (isLoggedin) {
            fetchNotifications();
            // Rafraîchir toutes les 30 secondes
            const interval = setInterval(fetchNotifications, 30000);
            return () => clearInterval(interval);
        }
    }, [isLoggedin]);

    const getAuthState = async () => {
        const token = localStorage.getItem('auth-token');
        if (token) {
            try {
                const {data} = await API.get('/api/is-auth');
                if (data.success) {
                    setIsLoggedin(true);
                    getUserData();
                }
            } catch (error) {
                localStorage.removeItem('auth-token');
                setIsLoggedin(false);
                setUserData(null);
            }
        }
    };

    useEffect(() => {
        getAuthState();
    }, [])

    // Effet pour vérifier et recharger userData
    useEffect(() => {
        const token = localStorage.getItem('auth-token');
        if (token) {
            setIsLoggedin(true);
            getUserData();
        }
    }, [isLoggedin]); // Dépendance à isLoggedin

    const getUserData = async () => {
        try {
            const {data} = await API.get('/api/user/data');
            if (data.success) {
                setUserData(data.userData);
            } else {
                setIsLoggedin(false);
                localStorage.removeItem('auth-token');
            }
        } catch (error) {
            setIsLoggedin(false);
            localStorage.removeItem('auth-token');
        }
    };

    // Fonction pour récupérer tous les utilisateurs
    const fetchAllUsers = async () => {
        setUsersLoading(true);
        try {
            const response = await API.get('/api/user');
            const users = response.data;
            
            // Créer un mapping des IDs utilisateurs vers leurs données
            const userMap = {};
            users.forEach(user => {
                userMap[user._id] = {
                    name: user.name,
                    email: user.email,
                    company: user.company,
                    // Ajoutez d'autres propriétés dont vous avez besoin
                };
            });
            
            setUsersMap(userMap);
            setUsersError(null);
        } catch (error) {
            console.error('Error fetching users:', error);
            setUsersError('Impossible de charger les données des utilisateurs');
        } finally {
            setUsersLoading(false);
        }
    };

    // Obtenir le nom d'un utilisateur à partir de son ID
    const getUserName = (userId) => {
        // Si nous avons les informations de cet utilisateur dans notre carte
        if (usersMap[userId]) {
            return usersMap[userId].name;
        }
        
        // Si c'est l'utilisateur actuel
        if (userData && userId === userData._id) {
            return userData.name;
        }
        
        // Valeur par défaut
        return "Utilisateur inconnu";
    };

    // Obtenir l'email d'un utilisateur à partir de son ID
    const getUserEmail = (userId) => {
        if (usersMap[userId]) {
            return usersMap[userId].email;
        }
        
        if (userData && userId === userData._id) {
            return userData.email;
        }
        
        return "Email non disponible";
    };

    const isAdmin = () => {
      return userData && (userData.role === 'admin' || userData.role === 'generalAdmin')
    }

    const isGeneralAdmin = () => {
        return userData && userData.role === 'generalAdmin';
    };

    // Rafraîchir manuellement la liste des utilisateurs
    const refreshUsers = () => {
        fetchAllUsers();
    };

    // Charger les utilisateurs au démarrage
    useEffect(() => {
        if (isLoggedin) {
            fetchAllUsers();
        }
    }, [isLoggedin]);

    const value = {
        backendUrl,
        isLoggedin, setIsLoggedin,
        userData, setUserData,
        getUserData,
        // Gestion des notifications
        notifications,
        unreadCount,
        fetchNotifications,
        markAsRead,
        markAsUnread,
        deleteNotification,
        markAllAsRead,
        // Gestion des utilisateurs
        usersMap,
        usersLoading,
        usersError,
        getUserName,
        getUserEmail,
        refreshUsers,
        isAdmin,
        isGeneralAdmin,

    }

    return (
        <AppContent.Provider value={value}>
            {props.children}
        </AppContent.Provider>
    )
}